import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Select from '~components/Form/components/Select';
import InputField from '~components/Form/components/InputField';
import NavTabs from '~components/NavTabs';
import ModalAction from '~components/Modal/ModalAction';
import { useConfigureFooter } from '~hooks/useHelpers';
import useFunilObraUnidadeMedida from '~fetchs/Funnel/useFunilObraUnidadeMedida';
import useFunilObraGrupoProduto from '~fetchs/useFunilObraGrupoProduto';
import { db } from '~db/db';
import { initialPath } from '~misc/paths';
import originControl from '~services/utils/originControl';

const baseSchemaShape = {
  funilObraGrupoProdutoId: yup
    .number()
    .typeError('Grupo Produto é obrigatório')
    .required('Grupo Produto é obrigatório'),
  funilObraUnidadeMedidaId: yup
    .number()
    .typeError('Unidade de Medida é obrigatória')
    .required('Unidade de Medida é obrigatória'),
  volumeMapeado: yup
    .number()
    .typeError('Volume Mapeado é obrigatório')
    .moreThan(0, 'O volume mapeado deve ser maior do que zero')
    .required('Volume Mapeado é obrigatório'),
};

const modalKind = {
  save: 'save',
};

const buildAcompanhamentoInitialValues = (group) => ({
  funilObraGrupoProdutoId: group?.funilObraGrupoProdutoId ?? '',
  funilObraUnidadeMedidaId: group?.funilObraUnidadeMedidaId ?? '',
  volumeMapeado: group?.volumeMapeado ?? '',
});

const FunnelEditGroup = () => {
  const formikRef = useRef();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { parameter: clienteId, id } = useParams();
  const { origens } = originControl();

  const [activeModal, setActiveModal] = useState(null);
  const grupoId = id ? Number(id) : null;

  const { optionFunilObraUnidadeMedida, funilObraUnidadeMedidaSelect, onChangeFunilObraUnidadeMedida, setSelectFunilObraUnidadeMedida } = useFunilObraUnidadeMedida({ formikRef });
  const { optionFunilObraGruposProduto, funilObraGrupoProdutoSelect, onChangeFunilObraGrupoProduto, SetSelectFunilObraGrupoProduto } = useFunilObraGrupoProduto({ formikRef });
  const [acompanhamentoGroup, setAcompanhamentoGroup] = useState(null);

  const initialValues = buildAcompanhamentoInitialValues(acompanhamentoGroup);
  const validationSchema = yup.object(baseSchemaShape);

  const tabs = [
    {
      name: 'Funil de Obras',
      link: globalThis.location.pathname,
    },
  ];

  const closeModal = () => setActiveModal(null);

  const handleSubmit = () => {
    setActiveModal(modalKind.save);
  };

  const confirmarSalvamento = async () => {
    const values = formikRef.current?.values;
    const groups = [];

    await db.funilObraDashboard.where('clienteId')
        .equals(parseInt(clienteId))
        .filter(
          e => e.funilObraGrupoProdutoId === parseInt(grupoId)
        )
        .each(g => groups.push({
          ...g,
          volumeMapeado: parseInt(values?.volumeMapeado ?? 0)
        }));

    if(groups?.length) {
      await db.funilObraDashboard.bulkPut(groups);
    }
    
    closeModal();
    const params = new URLSearchParams(searchParams);
    params.set("origem", origens.acompanhamento_mapeamento);
    navigate(`${initialPath}/funil-de-obras/${clienteId}/acompanhamento-mapeamento?${params.toString()}`);
  };

  useConfigureFooter({ form: true, formId: 'novo', submitLabel: 'Mapear', disabled: false });

  useEffect(() => {
    async function load() {
      if (grupoId == null || clienteId == null) {
        setAcompanhamentoGroup(null);
        return;
      }

      const oneGroup = await db.funilObraDashboard
        .where('clienteId')
        .equals(parseInt(clienteId))
        .filter(
          e => e.funilObraGrupoProdutoId === parseInt(grupoId)
        )
        .first();

      if (!oneGroup) {
        setAcompanhamentoGroup(null);
        return;
      }

      setAcompanhamentoGroup({
        funilObraGrupoProdutoId: oneGroup.funilObraGrupoProdutoId ?? '',
        funilObraUnidadeMedidaId: oneGroup.funilObraUnidadeMedidaId ?? '',
        volumeMapeado: oneGroup.volumeMapeado ?? '',
      });
    }

    load();
  }, [clienteId, grupoId]);

  useEffect(() => {
    if(acompanhamentoGroup && Object.entries(acompanhamentoGroup)?.length) {
      setSelectFunilObraUnidadeMedida(acompanhamentoGroup?.funilObraUnidadeMedidaId)
      SetSelectFunilObraGrupoProduto(acompanhamentoGroup?.funilObraGrupoProdutoId)
    }
  }, [acompanhamentoGroup]);

  return (
    <section className="container">
      <NavTabs navs={tabs} customClasses={'!flex-none !w-[40%]'} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        innerRef={formikRef}
        onSubmit={handleSubmit}
      >
        {({ errors, touched }) => {
          return (
            <Form id="novo">
              <div className="relative containerForm mt-4 rounded-xl bg-white p-7">

                <div className="sm:flex justify-between py-3">
                  <h2 className="text-2xl font-bold title pb-6">
                    Dados Gerais
                  </h2>
                </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      options={optionFunilObraGruposProduto}
                      label="Grupo Produto:"
                      name="funilObraGrupoProdutoId"
                      placeholder="Grupo Produto"
                      isDisabled
                      isRequired
                      onChange={(e) => onChangeFunilObraGrupoProduto(e)}
                      spacingControl={true}
                      errors={errors.funilObraGrupoProdutoId}
                      touched={touched.funilObraGrupoProdutoId}
                      {...funilObraGrupoProdutoSelect}
                    />
                  <div />

                  <Select
                    options={optionFunilObraUnidadeMedida}
                    label="Unidade de Medida:"
                    name="funilObraUnidadeMedidaId"
                    placeholder="Unidade de Medida"
                    isDisabled
                    isRequired
                    onChange={(e) => onChangeFunilObraUnidadeMedida(e)}
                    spacingControl={true}
                    errors={errors.funilObraUnidadeMedidaId}
                    touched={touched.funilObraUnidadeMedidaId}
                    {...funilObraUnidadeMedidaSelect}
                  />

                  <InputField
                    labelName="Volume Mapeado:"
                    tooltip="Volume total estimado para obra"
                    inputName="volumeMapeado"
                    inputMask="offmask"
                    inputType="number"
                    isRequired
                    placeholder="Volume Mapeado"
                    onWheel={(e) => e.target.blur()}
                  />
                </div>                
              </div>
            </Form>
          );
        }}
      </Formik>

      <ModalAction
        isOpen={activeModal === modalKind.save}
        onClose={closeModal}
        title="Confirmar salvamento"
        sub="Deseja confirmar o salvamento dos dados?"
        status="success"
        buttonOne="Confirmar"
        buttonTwo="Cancelar"
        linkOne={confirmarSalvamento}
        linkTwo={closeModal}
      />
    </section>
  );
};

export default FunnelEditGroup;
